import { useMemo, useState } from 'react';
import { trackerDefaults } from '../../../utils/QRFunctions/defaultSurveyTrackerValues';
import { useAuth } from '../../../utils/Auth';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage } from '../../../utils/hooks/useLocalStorage';
import { typeGroups } from './types';
import styles from './TypeSelect.module.css';

export default function TypeSelect() {
    const { dataDispatch, qr_data, orgObj } = useAuth();
    const [, setSearchParams ] = useSearchParams();
    const plan_id = useMemo(() => orgObj?.plan?.plan_id, [orgObj?.plan?.plan_id]);
    const qr_type = useMemo(() => qr_data?.data?.qr_type, [qr_data?.data?.qr_type]);
    const disabledQRs = useMemo(() => plan_id < 1,[plan_id]);
    const defaultTrackerTypes = ['workout-log','service-log','vehicle-log','maintenance-tracker','garden-label'];
    const defaultSurveyTypes = ['review','bug-report','contact-form'];
    const [ storedQrType, setStoredQrType ] = useLocalStorage('qr_type');
    
    // Filter state management - default to The Essentials and Popular
    const [selectedGroups, setSelectedGroups] = useState(new Set(['The Essentials', 'Popular']));
    
    // Mobile filter collapse state - collapsed by default
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    
    // Groups that should appear in the filter sidebar (excluding defaults)
    const filterableGroups = useMemo(() => {
        return typeGroups.filter(group => 
            group.header !== 'The Essentials' && group.header !== 'Popular'
        );
    }, []);
    
    // Get all types for the unified view
    const allTypes = useMemo(() => {
        return typeGroups.flatMap(group => group.types);
    }, []);
    
    // Get filtered types based on selected groups
    const filteredTypes = useMemo(() => {
        if (selectedGroups.size === 0) {
            return allTypes; // Show all if no filters selected
        }
        return typeGroups
            .filter(group => selectedGroups.has(group.header))
            .flatMap(group => group.types);
    }, [selectedGroups, allTypes]);
    
    // Check if we're in filtered mode (non-default filters active)
    const isFiltered = filterableGroups.some(group => selectedGroups.has(group.header));

    const handleSelect = (type) => {
        if (qr_type !== type) {
            if (defaultTrackerTypes.includes(type)) {
                dataDispatch({ type: 'tracker', data: { qr_type: 'tracker', columns: trackerDefaults[type] } });
            } else if (defaultSurveyTypes.includes(type)) {
                dataDispatch({ type: 'survey', data: { qr_type: 'survey', questions: trackerDefaults[type] }});
            };
        };
        setStoredQrType(type);
        dataDispatch({ type: 'qr_type', data: type });
        setSearchParams({ tab: 'configure'})
    };
    
    const handleGroupToggle = (groupHeader) => {
        setSelectedGroups(prev => {
            const newSet = new Set(prev);

            // If this is the first non-default filter being selected, clear defaults
            if (!newSet.has(groupHeader) && 
                (newSet.has('The Essentials') || newSet.has('Popular')) &&
                !filterableGroups.some(group => newSet.has(group.header))) {
                newSet.clear();
            }

            if (newSet.has(groupHeader)) {
                newSet.delete(groupHeader);
            } else {
                newSet.add(groupHeader);
            }

            // If no filterable groups are selected, restore defaults
            if (!filterableGroups.some(group => newSet.has(group.header))) {
                newSet.add('The Essentials');
                newSet.add('Popular');
            }
            
            return newSet;
        });
    };
    
    const clearAllFilters = () => {
        // Clear all means back to defaults (Essentials + Popular)
        setSelectedGroups(new Set(['The Essentials', 'Popular']));
    };

    return (
        <div className={styles.container}>
            
            <div className={styles.mainLayout}>
                {/* Sidebar Filters */}
                <div className={styles.sidebar}>
                    <button 
                        className={styles.mobileFilterToggle}
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                    >
                        <span>Filter by Category</span>
                        <i className={`fas fa-chevron-${filtersExpanded ? 'up' : 'down'}`}></i>
                    </button>
                    
                    <div className={styles.desktopFilterHeader}>
                        <h5>Filter by Category</h5>
                        {isFiltered && (
                            <button 
                                className={styles.clearFilters} 
                                onClick={clearAllFilters}
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                    
                    <div className={`${styles.filterList} ${filtersExpanded ? styles.expanded : ''}`}>
                        {filterableGroups.map(group => (
                            <label key={group.header} className={styles.filterItem}>
                                <input
                                    type="checkbox"
                                    checked={selectedGroups.has(group.header)}
                                    onChange={() => handleGroupToggle(group.header)}
                                />
                                <span className={styles.checkmark}></span>
                                <span className={styles.filterLabel}>{group.header}</span>
                                <span className={styles.typeCount}>({group.types.length})</span>
                            </label>
                        ))}
                        {isFiltered && filtersExpanded && (
                            <button 
                                className={styles.mobileClearFilters} 
                                onClick={clearAllFilters}
                            >
                                Clear All Filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Main Content Area */}
                <div className={styles.mainContent}>
                    {/* Show default groups (Essentials & Popular) with headers */}
                    {selectedGroups.has('The Essentials') && (
                        <div className={styles.groupSection}>
                            <div className={styles.groupHeader}>The Essentials</div>
                            <div className={styles.btnWrap}>
                                {typeGroups
                                    .find(group => group.header === 'The Essentials')
                                    ?.types.map(type => (
                                        <button 
                                            key={type.type} 
                                            className={`${styles.typeBtn} ${qr_type === type.type ? styles.chosen : ''}`} 
                                            onClick={() => handleSelect(type.type)}
                                        >
                                            <i className={type.icon} />
                                            {type.label}
                                        </button>
                                    ))}
                            </div>
                        </div>
                    )}
                    
                    {selectedGroups.has('Popular') && (
                        <div className={styles.groupSection}>
                            <div className={styles.groupHeader}>Popular</div>
                            <div className={styles.btnWrap}>
                                {typeGroups
                                    .find(group => group.header === 'Popular')
                                    ?.types.map(type => (
                                        <button 
                                            key={type.type} 
                                            className={`${styles.typeBtn} ${qr_type === type.type ? styles.chosen : ''}`} 
                                            onClick={() => handleSelect(type.type)}
                                        >
                                            <i className={type.icon} />
                                            {type.label}
                                        </button>
                                    ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Show filtered types as one unified list with "Selected QRs" header */}
                    {isFiltered && (
                        <div className={styles.filteredSection}>
                            <div className={styles.groupHeader}>Selected QR Types</div>
                            <div className={styles.allTypesBtnWrap}>
                                {typeGroups
                                    .filter(group => selectedGroups.has(group.header) && 
                                             group.header !== 'The Essentials' && 
                                             group.header !== 'Popular')
                                    .flatMap(group => group.types)
                                    .map(type => (
                                        <button 
                                            key={type.type} 
                                            className={`${styles.typeBtn} ${qr_type === type.type ? styles.chosen : ''}`} 
                                            onClick={() => handleSelect(type.type)}
                                        >
                                            <i className={type.icon} />
                                            {type.label}
                                        </button>
                                    ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Explore section - always visible */}
                    <div className={styles.exploreSection}>
                        <div className={styles.exploreHeader}>
                            <div className={styles.exploreTitle}>Explore All QR Types</div>
                            <span className={styles.typeCountBadge}>
                                {allTypes.length} Available Types
                            </span>
                        </div>
                        <div className={styles.allTypesBtnWrap}>
                            {allTypes.map(type => (
                                <button 
                                    key={type.type} 
                                    className={`${styles.typeBtn} ${qr_type === type.type ? styles.chosen : ''}`} 
                                    onClick={() => handleSelect(type.type)}
                                >
                                    <i className={type.icon} />
                                    {type.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};